import { useEffect, useRef, useState } from "react";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";

import { ElectricityData, GasData } from "../../services/salesDocuments-v2.service";
import { Meter } from "../../types/salesDocuments.types";

import { MeterContextProvider, useMeterContext } from "./meterContext";
import { ConsumptionComponent } from "./meterForm/consumptionComponent";
import { ExtraDetailsComponent } from "./meterForm/extraDetailsComponent";
import { FitChargeComponent } from "./meterForm/fitChargeComponent";
import { HhMeterComponent } from "./meterForm/hhMeterComponent";
import { MeterIdComponent } from "./meterForm/meterIdComponent";
import { MeterNameComponent } from "./meterForm/nameComponent";
import { NonCommodityCostComponent } from "./meterForm/nonCommodityCostComponent";
import { SimpleConsumptionComponent } from "./meterForm/simpleConsumptionComponent";
import { SupplierComponent } from "./meterForm/supplierComponent";
import { UpliftRateComponent } from "./meterForm/upliftRateComponent";
import { meterFieldConfig, MeterFieldKeys } from "./salesDocuments.constants";

interface MeterProps {
  meter: Meter;
  isActive: boolean;
  isValidated: boolean;
  onNameChange: (id: number, name: string) => void;
  registerPayloadGetter: (meterId: number, getter: () => ElectricityData | GasData | null) => void;
  registerValidityChecker: (meterId: number, checker: () => boolean) => void;
  onDelete: (meterId: number) => void;
}

interface MeterFormInnerProps {
  meter: Meter;
  isActive: boolean;
  isValidated: boolean;
  onNameChange: (id: number, name: string) => void;
  onDelete: (meterId: number) => void;
  registerValidityChecker: (meterId: number, checker: () => boolean) => void;
}

const MeterForm: React.FC<MeterProps> = ({
  meter,
  isActive,
  isValidated,
  onNameChange,
  registerPayloadGetter,
  registerValidityChecker,
  onDelete,
}) => {
  return (
    <MeterContextProvider meter={meter} registerPayloadGetter={registerPayloadGetter}>
      <MeterFormInner
        meter={meter}
        isActive={isActive}
        onNameChange={onNameChange}
        isValidated={isValidated}
        onDelete={onDelete}
        registerValidityChecker={registerValidityChecker}
      />
    </MeterContextProvider>
  );
};

const MeterFormInner: React.FC<MeterFormInnerProps> = ({
  meter,
  isActive,
  onNameChange,
  isValidated,
  onDelete,
  registerValidityChecker,
}) => {
  const ctx = useMeterContext();
  const [showRateError, setShowRateError] = useState(false);

  const hasRateRef = useRef(false);

  useEffect(() => {
    hasRateRef.current = [
      ctx.dayRate,
      ctx.nightRate,
      ctx.eveningWeekendRate,
      ctx.eveningWeekendNightRate,
    ].some((r) => r.rate !== "" && r.consumption !== "");
  }, [ctx.dayRate, ctx.nightRate, ctx.eveningWeekendRate, ctx.eveningWeekendNightRate]);

  useEffect(() => {
    registerValidityChecker(meter.id, () => {
      const form = document.getElementById(
        `meter-${meter.id.toString()}`,
      ) as HTMLFormElement | null;
      const formValid = form?.reportValidity() ?? true;
      const hasRate = hasRateRef.current;
      if (meter.fuelType === "electricity" && !hasRate) {
        setShowRateError(true);
      } else {
        setShowRateError(false);
      }
      return formValid && (meter.fuelType !== "electricity" || hasRate);
    });
  }, [meter.id, meter.fuelType, registerValidityChecker]);
  const hiddenStyle = isActive ? {} : { display: "none" };
  const hideComponents = !ctx.apiDataLoaded;

  const componentMap: Record<string, React.JSX.Element | null> = {
    [MeterFieldKeys.Name]: <MeterNameComponent meter={meter} onNameChange={onNameChange} />,
    [MeterFieldKeys.Id]: <MeterIdComponent meter={meter} />,
    [MeterFieldKeys.NonCommodity]: (
      <NonCommodityCostComponent meter={meter} hidden={hideComponents} />
    ),
    [MeterFieldKeys.HH]: <HhMeterComponent meter={meter} hidden={hideComponents} />,
    [MeterFieldKeys.Fit]: <FitChargeComponent meter={meter} hidden={hideComponents} />,
    [MeterFieldKeys.Standing]: <UpliftRateComponent meter={meter} hidden={true} />,
    [MeterFieldKeys.Consumption]: <ConsumptionComponent meter={meter} hidden={hideComponents} />,
    [MeterFieldKeys.SimpleConsumption]: (
      <SimpleConsumptionComponent meter={meter} hidden={hideComponents} />
    ),
    [MeterFieldKeys.Supplier]: <SupplierComponent meter={meter} hidden={hideComponents} />,
    [MeterFieldKeys.ExtraDetails]: <ExtraDetailsComponent meter={meter} hidden={hideComponents} />,
  };

  const componentPositions: Record<string, string> = {
    [MeterFieldKeys.Name]: "top",
    [MeterFieldKeys.Id]: "top", // TODO: Move api fields component to bottom - revoke from this component
    [MeterFieldKeys.NonCommodity]: "left",
    [MeterFieldKeys.HH]: "left",
    [MeterFieldKeys.Fit]: "left",
    [MeterFieldKeys.Standing]: "left",
    [MeterFieldKeys.Consumption]: "right",
    [MeterFieldKeys.SimpleConsumption]: "right",
    [MeterFieldKeys.Supplier]: "left",
    [MeterFieldKeys.ExtraDetails]: "bottom",
  };

  const fields = meterFieldConfig[meter.fuelType];

  return (
    <>
      <div style={hiddenStyle} className="sales-doc-section meter-data">
        <form
          id={`meter-${meter.id.toString()}`}
          data-fuel-type={meter.fuelType}
          className={isValidated ? "was-validated" : ""}
        >
          {/* Dumbest shit I ever did in my life - ordering controlled by placement -> order in fields */}
          <Row>
            {fields
              .filter((fieldName) => componentPositions[fieldName] === "top")
              .map((fieldName) => (
                <div key={fieldName}>{componentMap[fieldName]}</div>
              ))}
          </Row>
          <Row>
            <Col>
              {fields
                .filter((fieldName) => componentPositions[fieldName] === "left")
                .map((fieldName) => (
                  <div key={fieldName}>{componentMap[fieldName]}</div>
                ))}
            </Col>

            <Col>
              {fields
                .filter((fieldName) => componentPositions[fieldName] === "right")
                .map((fieldName) => (
                  <div key={fieldName}>{componentMap[fieldName]}</div>
                ))}
            </Col>
          </Row>
          <Row>
            {fields
              .filter((fieldName) => componentPositions[fieldName] === "bottom")
              .map((fieldName) => (
                <div key={fieldName}>{componentMap[fieldName]}</div>
              ))}
          </Row>

          <button
            type="button"
            className="sales-doc-btn-danger"
            onClick={() => {
              onDelete(meter.id);
            }}
          >
            🗑️ Delete This Meter
          </button>
        </form>
      </div>
      {/* Toast for no rate errors */}
      <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 9999 }}>
        <div
          className={`toast align-items-center text-bg-danger border-0 ${showRateError ? "show" : ""}`}
          role="alert"
        >
          <div className="d-flex">
            <div className="toast-body">
              At least one unit rate is required for meter {meter.id}.
            </div>
            <button
              type="button"
              className="btn-close btn-close-white me-2 m-auto"
              onClick={() => {
                setShowRateError(false);
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};
export default MeterForm;
